import {
  type BrandBrief,
  type BriefContent,
  DEFAULT_BRIEF_TYPE,
  getBrandBriefTypeLabel,
  mapContentForBriefInput,
} from 'src/data/brief';
import { type Customer, customerTrialEnded } from 'src/data/customer';
import {
  getSuggestedAnnotationsOriginLabel,
  type SuggestedAnnotationsOrigin,
} from 'src/data/performance';
import { getUserName, type User } from 'src/data/user';
import type { Workspace } from 'src/data/workspace';

import type {
  InfinityBrandBrief,
  InfinityBrief,
  InfinityCustomer,
  InfinityResource,
  InfinityUser,
  InfinityWorkspace,
  ResourceItem,
} from './types';

export const mapBriefForInfinity = (brief: BriefContent): InfinityBrief | undefined => {
  if (!brief) {
    return undefined;
  }

  const content = mapContentForBriefInput(DEFAULT_BRIEF_TYPE, brief);
  if (!content) {
    return undefined;
  }

  return {
    name: content.title,
    typeName: 'Product Description',
    body: content.body,
  };
};

export const mapBrandBriefForInfinity = (brief: BrandBrief): InfinityBrandBrief | undefined => {
  if (!brief) {
    return undefined;
  }

  return {
    id: brief.id,
    name: brief.name,
    typeName: brief.briefType ? getBrandBriefTypeLabel(brief.briefType) : '',
    body: brief.body,
  };
};

export const mapBrandBriefsForInfinity = (briefs: BrandBrief[]): InfinityBrandBrief[] => {
  if (!briefs || briefs.length === 0) {
    return [];
  }

  return briefs.map(mapBrandBriefForInfinity).filter((item) => item !== undefined);
};

export const mapResourcesForInfinity = (resources: ResourceItem[]): InfinityResource[] => {
  if (!resources || resources.length === 0) {
    return [];
  }

  return resources.map((resource) => ({
    id: resource.id,
    url: resource.url,
    typeName: getSuggestedAnnotationsOriginLabel(
      resource.websiteType as SuggestedAnnotationsOrigin
    ),
  }));
};

export const mapWorkspaceForInfinity = (workspace: Workspace): InfinityWorkspace => ({
  id: workspace.id,
  name: workspace.name,
});

export const mapWorkspacesForInfinity = (workspaces: Workspace[]): InfinityWorkspace[] => {
  if (!workspaces || workspaces.length === 0) {
    return [];
  }

  return workspaces.map(mapWorkspaceForInfinity);
};

export const mapUserForInfinity = (user: User): InfinityUser => ({
  id: user.id,
  name: getUserName(user),
  isConfirmed: user.isConfirmed,
});

export const mapCustomerForInfinity = (customer: Customer): InfinityCustomer => ({
  id: customer.id,
  name: customer.name,
  featureLimits: customer.featureLimits,
  trialEnded: customerTrialEnded(customer),
});
